import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'

export default function PatientDashboard() {
  const { user } = useAuthStore()

  // サンプルデータ
  const patientInfo = {
    researchId: 'R01166',
    patientId: '39-011',
    diseaseName: '潰瘍性大腸炎',
    treatment: 'FMT3回コース',
    transplantCount: 3,
    lastTransplant: '2025-06-26',
    nextAppointment: '2025-12-05',
    primaryDoctor: '吉岡 紘平',
    hospitalName: user?.hospitalName || 'Baseクリニック赤坂',
  }

  const recentRecords = [
    { id: 'rec-001', date: '2025-11-28', type: 'patient', summary: '体調良好、便通2回' },
    { id: 'rec-002', date: '2025-11-27', type: 'doctor', summary: '経過観察、次回予約確認' },
    { id: 'rec-003', date: '2025-11-26', type: 'patient', summary: '体調普通、便通1回' },
  ]

  const todayRecorded = recentRecords.some(
    (r) => r.date === new Date().toISOString().split('T')[0] && r.type === 'patient'
  )

  return (
    <div className="patient-dashboard">
      {/* ウェルカムセクション */}
      <section className="welcome-section">
        <div className="welcome-content">
          <h1 className="welcome-title">
            こんにちは、{user?.displayName}さん
          </h1>
          <p className="welcome-subtitle">
            今日の体調はいかがですか？
          </p>
        </div>
        {!todayRecorded ? (
          <Link to="/patient/daily-record" className="daily-record-cta">
            <span className="material-symbols-outlined">edit_note</span>
            今日の記録を入力する
          </Link>
        ) : (
          <div className="daily-record-done">
            <span className="material-symbols-outlined">check_circle</span>
            本日の記録は入力済みです
          </div>
        )}
      </section>

      <div className="dashboard-grid">
        {/* 治療情報カード */}
        <section className="dashboard-card treatment-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-symbols-outlined">medical_information</span>
              治療情報
            </h2>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">研究会ID</span>
                <span className="info-value">{patientInfo.researchId}</span>
              </div>
              <div className="info-item">
                <span className="info-label">患者ID</span>
                <span className="info-value">{patientInfo.patientId}</span>
              </div>
              <div className="info-item">
                <span className="info-label">病名</span>
                <span className="info-value">{patientInfo.diseaseName}</span>
              </div>
              <div className="info-item">
                <span className="info-label">治療</span>
                <span className="info-value">{patientInfo.treatment}</span>
              </div>
              <div className="info-item">
                <span className="info-label">移植回数</span>
                <span className="info-value">{patientInfo.transplantCount}回</span>
              </div>
              <div className="info-item">
                <span className="info-label">最終移植日</span>
                <span className="info-value">{patientInfo.lastTransplant}</span>
              </div>
            </div>
          </div>
        </section>

        {/* 担当医カード */}
        <section className="dashboard-card doctor-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-symbols-outlined">person</span>
              担当医
            </h2>
          </div>
          <div className="card-body">
            <div className="doctor-info">
              <div className="doctor-avatar">
                {patientInfo.primaryDoctor.charAt(0)}
              </div>
              <div className="doctor-details">
                <div className="doctor-name">{patientInfo.primaryDoctor}</div>
                <div className="doctor-hospital">{patientInfo.hospitalName}</div>
              </div>
            </div>
            {patientInfo.nextAppointment && (
              <div className="next-appointment">
                <span className="material-symbols-outlined">calendar_month</span>
                次回予約: {patientInfo.nextAppointment}
              </div>
            )}
          </div>
        </section>

        {/* 最近の記録カード */}
        <section className="dashboard-card records-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-symbols-outlined">history</span>
              最近の記録
            </h2>
            <Link to="/patient/records" className="card-link">
              すべて見る
            </Link>
          </div>
          <div className="card-body">
            <ul className="records-list">
              {recentRecords.map((record) => (
                <li key={record.id} className="record-item">
                  <div className="record-date">{record.date}</div>
                  <span className={`record-type record-type--${record.type}`}>
                    {record.type === 'patient' ? '患者入力' : '医師記録'}
                  </span>
                  <div className="record-summary">{record.summary}</div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* クイックアクションカード */}
        <section className="dashboard-card actions-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="material-symbols-outlined">bolt</span>
              クイックアクション
            </h2>
          </div>
          <div className="card-body">
            <div className="action-buttons">
              <Link to="/patient/daily-record" className="action-button">
                <span className="material-symbols-outlined">edit_note</span>
                今日の記録
              </Link>
              <Link to="/patient/questionnaire" className="action-button">
                <span className="material-symbols-outlined">quiz</span>
                アンケート
              </Link>
              <Link to="/patient/records" className="action-button">
                <span className="material-symbols-outlined">description</span>
                記録履歴
              </Link>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .patient-dashboard {
          max-width: 1200px;
          margin: 0 auto;
        }

        .welcome-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
          color: white;
          padding: var(--spacing-xl);
          border-radius: var(--border-radius-lg);
          margin-bottom: var(--spacing-xl);
        }

        .welcome-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: var(--spacing-xs);
        }

        .welcome-subtitle {
          opacity: 0.9;
        }

        .daily-record-cta {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md) var(--spacing-lg);
          background-color: white;
          color: #0891b2;
          border-radius: var(--border-radius-md);
          font-weight: 600;
          text-decoration: none;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .daily-record-cta:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        .daily-record-done {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md) var(--spacing-lg);
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: var(--border-radius-md);
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-lg);
        }

        .dashboard-card {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
          overflow: hidden;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-md) var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }

        .card-title {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 1rem;
          font-weight: 600;
        }

        .card-title .material-symbols-outlined {
          color: #0891b2;
        }

        .card-link {
          font-size: 0.875rem;
          color: #0891b2;
        }

        .card-body {
          padding: var(--spacing-lg);
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-md);
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }

        .info-label {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .info-value {
          font-size: 0.875rem;
          font-weight: 500;
        }

        .doctor-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-md);
        }

        .doctor-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background-color: #0891b2;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .doctor-name {
          font-weight: 600;
        }

        .doctor-hospital {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .next-appointment {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: #f0fdfa;
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          color: #0891b2;
        }

        .records-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .record-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          padding: var(--spacing-sm) 0;
          border-bottom: 1px solid var(--border-color);
        }

        .record-item:last-child {
          border-bottom: none;
        }

        .record-date {
          font-size: 0.875rem;
          color: var(--text-secondary);
          min-width: 100px;
        }

        .record-type {
          padding: 2px 8px;
          border-radius: var(--border-radius-sm);
          font-size: 0.6875rem;
          font-weight: 500;
        }

        .record-type--patient {
          background-color: #ccfbf1;
          color: #0d9488;
        }

        .record-type--doctor {
          background-color: #dbeafe;
          color: #2563eb;
        }

        .record-summary {
          font-size: 0.875rem;
          flex: 1;
        }

        .action-buttons {
          display: flex;
          gap: var(--spacing-md);
        }

        .action-button {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          background-color: #f0fdfa;
          border-radius: var(--border-radius-md);
          color: #0891b2;
          text-decoration: none;
          flex: 1;
          transition: background-color 0.2s;
        }

        .action-button:hover {
          background-color: #ccfbf1;
        }

        .action-button .material-symbols-outlined {
          font-size: 28px;
        }

        @media (max-width: 768px) {
          .welcome-section {
            flex-direction: column;
            text-align: center;
            gap: var(--spacing-md);
          }

          .dashboard-grid {
            grid-template-columns: 1fr;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  )
}



